import { useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { SwiperSlide } from "swiper/react";

import ArticleCard from "@components/cards/ArticleCard";
import styles from "@components/explore/Explore.module.css";
import CardSlides from "@components/layout/CardSlides";
import Heading1 from "@components/typography/Heading1";
import { SortByTimestampDirectionEnum } from "@typing/Enums";
import {
  ArticleContentTypeEnum,
  FocusAreaEnum,
  GetArticlesBasicQuery,
  GetRecommendedArticlesQuery
} from "@typing/Generated";
import { groupArticlesByFocusArea, sortByTimestamp } from "@utils/modelUtils";

type Article = NonNullable<NonNullable<NonNullable<GetArticlesBasicQuery["articles"]["edges"]>[number]>["node"]>;

type Props = {
  allArticles: Article[];
  fetchMore: () => void;
  journeyId: string;
  recommendedArticles: GetRecommendedArticlesQuery["recommendedArticles"];
};

type SectionProps = {
  articles: Article[];
  fetchMore: () => void;
  journeyId: string;
  titleKey: string;
};

const Section = ({ articles, fetchMore, journeyId, titleKey }: SectionProps) => {
  if (articles.length === 0) return null;

  return (
    <div className={styles.articleSection}>
      <div className={styles.titleSpacing}>
        <Heading1>
          <FormattedMessage id={titleKey} />
        </Heading1>
      </div>

      <CardSlides itemCount={articles.length} onSlideNextTransitionStart={fetchMore}>
        {articles.map(article => (
          <SwiperSlide key={article.id}>
            <ArticleCard article={article} journeyId={journeyId} />
          </SwiperSlide>
        ))}
      </CardSlides>
    </div>
  );
};

const Media = ({ allArticles, fetchMore, journeyId, recommendedArticles }: Props) => {
  const articlesGrouped = useMemo(() => groupArticlesByFocusArea(allArticles), [allArticles]);
  const keyOrder: FocusAreaEnum[] = [
    FocusAreaEnum.WELLBEING,
    FocusAreaEnum.MEDICAL,
    FocusAreaEnum.LEGAL,
    FocusAreaEnum.FUNCTIONAL,
    FocusAreaEnum.FINANCIAL,
    FocusAreaEnum.SOCIAL,
    FocusAreaEnum.UNDER_EIGHTEEN
  ];

  return (
    <div>
      <Section
        articles={recommendedArticles}
        fetchMore={fetchMore}
        journeyId={journeyId}
        titleKey="pages.explore.media.selectedForYou"
      />
      {keyOrder.map(key => {
        const articles = articlesGrouped[key];
        return (
          <Section
            articles={articles}
            fetchMore={fetchMore}
            journeyId={journeyId}
            key={key}
            titleKey={`models.focusArea.${key}`}
          />
        );
      })}

      <Section
        articles={sortByTimestamp(
          allArticles.filter(a => !a.webinarCompleted && a.contentType === ArticleContentTypeEnum.WEBINAR),
          webinar => webinar.webinarStartTime,
          SortByTimestampDirectionEnum.CHRONOLOGICAL
        )}
        fetchMore={fetchMore}
        journeyId={journeyId}
        titleKey="pages.explore.events.upcomingTitle"
      />
      <Section
        articles={sortByTimestamp(
          allArticles.filter(a => a.webinarCompleted && a.contentType === ArticleContentTypeEnum.WEBINAR),
          webinar => webinar.webinarStartTime
        )}
        fetchMore={fetchMore}
        journeyId={journeyId}
        titleKey="pages.explore.events.pastTitle"
      />
    </div>
  );
};

export default Media;
